import React from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  MONO,
  MULISH,
  STATUS_STYLES,
  STEP_ORDER,
  STEP_TITLES,
} from "./vpnDebugConstants";
import {
  VpnDebugRenderStatus,
  VpnDebugStep,
  VpnDebugStepId,
} from "./vpnDebugInterfaces";
import { VpnDebugStatusPill } from "./VpnDebugStatusPill";

interface Props {
  stepId: VpnDebugStepId;
  step: VpnDebugStep | undefined;
  /** True while the plane that owns this step has not reported yet. */
  isPending: boolean;
  /** Evidence starts open on the first failing step — the reason they came here. */
  autoExpandEvidence: boolean;
  /** Suppresses the rail below the last card. */
  isLast: boolean;
}

const RAIL_COL_PX = 34;

/** Number badge that carries the step's colour, so the ladder reads at a glance. */
const StepBadge = ({
  index,
  status,
}: {
  index: number;
  status: VpnDebugRenderStatus;
}) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const solid = status === "pass" || status === "fail" || status === "warn";

  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Mulish",
        fontWeight: 700,
        fontSize: 12,
        border: `2px solid ${solid ? style.color : "#d1d5db"}`,
        backgroundColor: solid ? style.color : "#fff",
        color: solid ? "#fff" : "#9aa0a6",
      }}
    >
      {index}
    </Box>
  );
};

export const VpnDebugStepCard = ({
  stepId,
  step,
  isPending,
  autoExpandEvidence,
  isLast,
}: Props) => {
  // The server's title wins once the step has run. For operators it is the
  // same text as STEP_TITLES; for customers it is the customer-safe wording
  // ("Connection", "Traffic from your site") the projection sends on purpose.
  const title = step?.title || STEP_TITLES[stepId] || stepId;
  // A step the next plane is about to run must not sit there showing the
  // previous plane's "skipped", which reads as a finished, negative result.
  const status: VpnDebugRenderStatus = isPending
    ? "running"
    : step
    ? step.status
    : "pending";

  const [evidenceOpen, setEvidenceOpen] = React.useState(autoExpandEvidence);
  // Re-sync when a new run changes which step is the first failure.
  React.useEffect(
    () => setEvidenceOpen(autoExpandEvidence),
    [autoExpandEvidence]
  );

  const hasEvidence =
    step != null &&
    step.evidence != null &&
    Object.keys(step.evidence).length > 0;
  const accent = (STATUS_STYLES[status] || STATUS_STYLES.pending).color;
  const isQuiet = status === "pending" || status === "skipped";

  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      {/* Rail: badge plus the connector that makes the seven checks read as a ladder. */}
      <Box
        sx={{
          width: RAIL_COL_PX,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <StepBadge index={STEP_ORDER.indexOf(stepId) + 1} status={status} />
        {!isLast && (
          <Box
            sx={{ flex: 1, width: "2px", backgroundColor: "#e3e7ee", mt: 0.5 }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pb: isLast ? 0 : 2,
          opacity: isQuiet ? 0.72 : 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            minHeight: 26,
          }}
        >
          <Typography
            sx={{ ...MULISH, fontWeight: 700, fontSize: 15, color: "#001943" }}
          >
            {title}
          </Typography>
          <VpnDebugStatusPill status={status} />
          {step?.partial && (
            <Box
              component="span"
              title="Observed, not decode-verified"
              sx={{
                ...MULISH,
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid #b25e00",
                color: "#b25e00",
                borderRadius: "999px",
                px: 1,
                py: "1px",
              }}
            >
              Partial
            </Box>
          )}
          {step && step.decisive === false && (
            <Box
              component="span"
              title="Cannot move the final verdict on its own — firewalls routinely drop ICMP, and plenty of working NVR channels expose no snapshot."
              sx={{
                ...MULISH,
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid #d1d5db",
                color: "#6b7280",
                borderRadius: "999px",
                px: 1,
                py: "1px",
              }}
            >
              Not decisive
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {step?.duration_ms != null && (
            <Typography
              sx={{ ...MULISH, ...MONO, fontSize: 12, color: "#9aa0a6" }}
            >
              {Math.round(step.duration_ms)} ms
            </Typography>
          )}
        </Box>

        {isPending ? (
          <Typography
            sx={{ ...MULISH, fontSize: 13, color: "#6b7280", mt: 0.5 }}
          >
            Running on the gateway…
          </Typography>
        ) : !step ? (
          <Typography
            sx={{ ...MULISH, fontSize: 13, color: "#9aa0a6", mt: 0.5 }}
          >
            Not started.
          </Typography>
        ) : step.status === "skipped" ? (
          <Typography
            sx={{
              ...MULISH,
              fontSize: 13,
              color: "#6b7280",
              fontStyle: "italic",
              mt: 0.5,
            }}
          >
            {step.skipped_reason || "Not run."}
          </Typography>
        ) : (
          <>
            <Typography
              sx={{
                ...MULISH,
                fontSize: 14,
                color: "#283e6d",
                mt: 0.5,
                fontWeight: 600,
              }}
            >
              {step.summary}
            </Typography>
            {step.diagnosis && (
              <Typography
                sx={{
                  ...MULISH,
                  fontSize: 13,
                  color: "#3c4858",
                  mt: 0.75,
                  lineHeight: 1.5,
                }}
              >
                {step.diagnosis}
              </Typography>
            )}
            {step.next_action && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.25,
                  borderRadius: "6px",
                  borderLeft: `3px solid ${accent}`,
                  backgroundColor: "#f8f9fb",
                }}
              >
                <Typography
                  sx={{
                    ...MULISH,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    color: "#6b7280",
                  }}
                >
                  NEXT ACTION
                </Typography>
                <Typography
                  sx={{
                    ...MULISH,
                    fontSize: 13,
                    color: "#283e6d",
                    mt: 0.25,
                    lineHeight: 1.5,
                  }}
                >
                  {step.next_action}
                </Typography>
              </Box>
            )}
          </>
        )}

        {hasEvidence && (
          <Box sx={{ mt: 1 }}>
            <Box
              component="button"
              type="button"
              aria-expanded={evidenceOpen}
              onClick={() => setEvidenceOpen((prev) => !prev)}
              sx={{
                ...MULISH,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                fontSize: 12,
                fontWeight: 600,
                color: "#283e6d",
                background: "none",
                border: "none",
                p: 0,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Evidence
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transition: "transform 150ms",
                  transform: evidenceOpen ? "rotate(180deg)" : "none",
                }}
              />
            </Box>
            <Collapse in={evidenceOpen} unmountOnExit>
              <Box
                component="pre"
                sx={{
                  ...MONO,
                  backgroundColor: "#f4f6f8",
                  border: "1px solid #e3e7ee",
                  borderRadius: "6px",
                  p: 1.25,
                  mt: 0.75,
                  mb: 0,
                  maxHeight: 300,
                  overflow: "auto",
                  fontSize: 11.5,
                  lineHeight: 1.55,
                  color: "#3c4858",
                }}
              >
                {JSON.stringify(step?.evidence, null, 2)}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};
